/* RESPONSIBLE TEAM: team-ai-agent */
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import { attr, type SyncHasMany } from '@ember-data/model';
import Step, {
  type CreateStepParams,
  IsCustomerFacing,
} from 'embercom/models/operator/visual-builder/step';
import type Store from '@ember-data/store';
import { buildValidations, validator } from 'ember-cp-validations';
import { SUPPORTED_CHANNELS } from 'embercom/lib/operator/custom-bots/constants-2';
import { type Block } from 'embercom/models/common/blocks/block';
import type Localization from '../../localizable-step/localization';
import { inject as service } from '@ember/service';
import type WorkflowLocalizationService from 'embercom/services/workflow-localization-service';

/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
// eslint-disable-next-line no-restricted-imports
import { getApp, getIntlService } from 'embercom/lib/container-lookup';

const Validations: Validations<$TSFixMe> = buildValidations({
  ticketTypeId: [
    validator('presence', {
      presence: true,
      disabled: computed('model.endingType', function (this: { model: AnswerTerminal }) {
        return this.model.endingType !== 'send_ticket';
      }),
      messageKey: 'operator.workflows.visual-builder.validations.ticket-type-missing',
    }),
  ],
  endingType: [
    validator('visual-builder/send-ticket-channel-is-valid', {
      disabled: computed('model.endingType', function (this: { model: AnswerTerminal }) {
        return this.model.endingType !== 'send_ticket';
      }),
    }),
  ],
});

type EndingType =
  | 'show_standard_replies'
  | 'pass_to_team'
  | 'resolve'
  | 'finish_answer'
  | 'send_ticket';

export default class AnswerTerminal extends Step.extend(Validations) {
  @service declare workflowLocalizationService: WorkflowLocalizationService;

  @attr('string') declare endingType: EndingType;
  @fragmentArray('rules/rule') declare followUpRules: SyncHasMany<any>;
  @fragment('rules/rule') declare simpleActionRule?: any;
  @attr('string') declare ticketTypeId?: string;
  @attr('boolean') declare disableComposer?: boolean;
  @attr('boolean') declare progressToTriage?: boolean; // Setting currently hardcoded to true upon step creation for standalone workspaces. Can be exposed as a toggle for the main platform later on
  @fragmentArray('common/blocks/block', { polymorphic: true, typeKey: 'modelKey' })
  declare blocks: SyncHasMany<Block>;
  @attr('string') declare step_version: string | null;

  @fragmentArray('operator/localizable-step/localization')
  declare localizations: SyncHasMany<Localization>;

  get localization() {
    return this.localizations.find(
      (localization) => localization.locale === this.workflowLocalizationService.selectedLocaleId,
    );
  }

  get canUseV2Ui() {
    let isAiAgentWorkflow = this.group?.workflow?.isAiAgentWorkflow;
    return this.step_version === 'v2' && isAiAgentWorkflow;
  }

  get localBlocks() {
    return this.localization?.blocks || this.blocks || [];
  }

  get jsonBlocks() {
    return this.localBlocks.serialize();
  }

  get serializedBlocks() {
    // We only need to serialize the blocks if they are a FragmentArray (in tests they are already serialized)
    if (this.localBlocks && this.localBlocks.type === 'common/blocks/block') {
      return this.localBlocks.serialize();
    } else {
      return this.localBlocks;
    }
  }

  get supportedChannels() {
    return SUPPORTED_CHANNELS.answerTerminal;
  }

  static get requiredBillingFeature() {
    let app = getApp();

    if (app.canUseAiAgentWorkflows) {
      return 'ai_answers_billing_feature';
    }

    return 'answer_workflows';
  }

  get analyticsData() {
    return {
      ...super.analyticsData,
      ending_type: this.endingType,
      ticket_type_id: this.ticketTypeId,
    };
  }

  get hasSimpleActionRule() {
    return !!this.simpleActionRule;
  }

  static get isStepGroupEnding() {
    return true;
  }

  static get isStepTerminal() {
    return true;
  }

  static get canBeCustomerFacing() {
    return IsCustomerFacing.Yes;
  }

  static createNewStep(store: Store, params?: CreateStepParams): AnswerTerminal {
    let intl = getIntlService();
    let createV2Step = params?.shouldCreateV2TerminalStep;
    let defaultEndingType = createV2Step ? 'finish_answer' : 'show_standard_replies';

    let stepParams: Record<string, any> = {
      type: 'operator/visual-builder/step/answer-terminal',
      endingType: defaultEndingType,
      step_version: createV2Step ? 'v2' : '',
    };

    if (createV2Step) {
      stepParams.blocks = [
        store.createFragment('common/blocks/paragraph', {
          type: 'paragraph',
          text: intl.t('operator.workflows.visual-builder.finish-answer-default-follow-up-message'),
        }),
      ];
    }

    return store.createRecord('operator/visual-builder/step/answer-terminal', stepParams);
  }
}
